import { useEffect } from 'react';
import { useI18n } from './I18nContext';

const SKIP_TEXT_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA', 'CODE', 'PRE']);
const TRANSLATABLE_ATTRIBUTES = ['placeholder', 'title', 'aria-label', 'alt', 'value'];
const ORIGINAL_ATTRIBUTE_PREFIX = 'data-i18n-orig-';

const originalTextMap = new WeakMap();
// Text nodes whose current value was written by applyTextTranslation itself (not by
// React). The characterData observer uses this to tell its own writes apart from
// external ones unambiguously, by provenance rather than by guessing by content —
// see the observer's characterData branch for why content-based guessing breaks.
const selfWrittenTextNodes = new WeakSet();

const shouldTranslateNodeText = (node) => {
  const parent = node.parentElement;
  if (!parent) return false;

  if (parent.closest('[data-no-auto-i18n="true"]')) return false;
  if (SKIP_TEXT_TAGS.has(parent.tagName)) return false;
  // Never touch text inside a contenteditable region (rich-text editors like Quill).
  // The observer would otherwise cache the first typed letter as the "original" and
  // revert every subsequent keystroke back to it.
  if (parent.isContentEditable) return false;

  return true;
};

const shouldTranslateValueAttribute = (element) => {
  if (element.tagName !== 'INPUT') return false;

  const inputType = (element.getAttribute('type') || '').toLowerCase();
  return ['button', 'submit', 'reset'].includes(inputType);
};

const applyTextTranslation = (node, translateLiteral) => {
  if (!shouldTranslateNodeText(node)) return;

  if (!originalTextMap.has(node)) {
    originalTextMap.set(node, node.nodeValue || '');
  }

  const original = originalTextMap.get(node) || '';
  const translated = translateLiteral(original);

  if (node.nodeValue !== translated) {
    selfWrittenTextNodes.add(node);
    node.nodeValue = translated;
  }
};

const applyAttributeTranslation = (element, translateLiteral) => {
  if (element.closest('[data-no-auto-i18n="true"]')) return;

  TRANSLATABLE_ATTRIBUTES.forEach((attr) => {
    if (attr === 'value' && !shouldTranslateValueAttribute(element)) {
      return;
    }

    if (!element.hasAttribute(attr)) return;

    const originalAttributeName = `${ORIGINAL_ATTRIBUTE_PREFIX}${attr}`;

    if (!element.hasAttribute(originalAttributeName)) {
      element.setAttribute(originalAttributeName, element.getAttribute(attr) || '');
    }

    const original = element.getAttribute(originalAttributeName) || '';
    const translated = translateLiteral(original);

    if (element.getAttribute(attr) !== translated) {
      element.setAttribute(attr, translated);
    }
  });
};

const applyTranslationsRecursively = (startNode, translateLiteral) => {
  if (!startNode) return;

  const stack = [startNode];

  while (stack.length > 0) {
    const node = stack.pop();

    if (node.nodeType === Node.TEXT_NODE) {
      applyTextTranslation(node, translateLiteral);
      continue;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      continue;
    }

    applyAttributeTranslation(node, translateLiteral);

    const children = node.childNodes;
    for (let i = children.length - 1; i >= 0; i -= 1) {
      stack.push(children[i]);
    }
  }
};

const LegacyLiteralTranslator = () => {
  const { locale, translateLiteral } = useI18n();

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    const root = document.body;
    if (!root) return undefined;

    applyTranslationsRecursively(root, translateLiteral);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            applyTranslationsRecursively(node, translateLiteral);
          });
          return;
        }

        if (mutation.type === 'characterData') {
          const node = mutation.target;
          // Was this mutation caused by our own applyTextTranslation write, or by
          // something external (React re-rendering with new source text, e.g. a page
          // title or a toggled label)? Provenance — not content — is the only reliable
          // signal: two different English strings can both be valid dictionary values
          // (e.g. a "Play"/"Pause" toggle), so guessing "is this text one of our known
          // translations" is not a safe way to detect our own writes. Only reset the
          // cached "original" on genuinely external mutations — resetting on our own
          // write would poison the cache with translated (non-English) text, permanently
          // breaking future lookups for this node (they're keyed on English source text).
          if (selfWrittenTextNodes.has(node)) {
            selfWrittenTextNodes.delete(node);
          } else if (originalTextMap.has(node)) {
            originalTextMap.set(node, node.nodeValue);
          }
          applyTextTranslation(node, translateLiteral);
          return;
        }

        if (mutation.type === 'attributes') {
          applyAttributeTranslation(mutation.target, translateLiteral);
        }
      });
    });

    observer.observe(root, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: TRANSLATABLE_ATTRIBUTES
    });

    return () => {
      observer.disconnect();
    };
  }, [locale, translateLiteral]);

  return null;
};

export default LegacyLiteralTranslator;
